import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Edit, Trash2, Download, CheckCircle, Clock, Package, AlertTriangle, Camera, Calendar, X, RotateCcw, Copy, FileText, MessageCircle, Store } from "lucide-react";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import LojaSelector from "@/components/LojaSelector";
import LoadingSpinner from "@/components/LoadingSpinner";
import { gerarComprovante, gerarComprovanteBlob } from "@/lib/gerarComprovante";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { logError } from "@/lib/logger";
import { useLojas, useLojasInfo } from "@/hooks/useLojas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Receitas = () => {
  const LOJAS_GATE = useLojas();
  const { empresa: empresaNome } = useLojasInfo();
  const [receitas, setReceitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [lojaSelecionada, setLojaSelecionada] = useState("Todas as lojas");
  const [statusFiltro, setStatusFiltro] = useState("Todos");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [filtroDataAtivo, setFiltroDataAtivo] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [lojaSelecionadaGate, setLojaSelecionadaGate] = useState(false);
  const buscaAplicada = useRef("");
  const navigate = useNavigate();

  const getValidadeBadge = (receita) => {
    const dataReceita = receita.data_receita || new Date(receita.data_registro).toISOString().split("T")[0];
    const expDate = new Date(dataReceita + "T12:00:00");
    expDate.setDate(expDate.getDate() + 365);
    const hoje = new Date();
    const diasRestantes = Math.ceil((expDate - hoje) / (1000 * 60 * 60 * 24));
    if (diasRestantes <= 0) return { label: "Expirada", color: "bg-red-100 text-red-700 border-red-200" };
    if (diasRestantes <= 30) return { label: `Vence em ${diasRestantes}d`, color: "bg-amber-100 text-amber-700 border-amber-200" };
    return { label: "Valida", color: "bg-emerald-100 text-emerald-700 border-emerald-200" };
  };

  const isAtrasado = (receita) => {
    if ((receita.status || "Em Produção") !== "Em Produção") return false;
    const ref = receita.status_atualizado_em || receita.data_registro;
    if (!ref) return false;
    const dt = new Date(ref);
    const diff = (Date.now() - dt.getTime()) / (1000 * 60 * 60 * 24);
    return diff > 7;
  };

  const toggleStatus = async (receita) => {
    const novoStatus = receita.status === "Em Produção" ? "Pronto para Entrega" : "Em Produção";
    try {
      await axios.patch(`${API}/receitas/${receita.id}/status?status=${encodeURIComponent(novoStatus)}`);
      toast.success(`Status alterado para: ${novoStatus}`);
      carregarReceitas(searchTerm);
    } catch {
      toast.error("Erro ao alterar status");
    }
  };

  const enviarComprovanteWhatsApp = async (receita) => {
    const digits = String(receita.telefone_cliente || "").replace(/\D/g, "");
    const numero = digits.startsWith("55") ? digits : `55${digits}`;
    const msg = `Olá${receita.nome_cliente ? " " + receita.nome_cliente : ""}! Segue o comprovante da sua compra${empresaNome ? " na " + empresaNome : ""}.`;
    try {
      const { blob, nome } = await gerarComprovanteBlob(receita);
      const file = new File([blob], nome, { type: "application/pdf" });
      // Celular: compartilhamento nativo envia o PDF direto ao WhatsApp
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Comprovante de Compra", text: msg });
        return;
      }
      // Desktop: baixa o PDF e abre a conversa do WhatsApp para anexar
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nome;
      a.click();
      URL.revokeObjectURL(url);
      if (digits) {
        window.open(`https://wa.me/${numero}?text=${encodeURIComponent(msg)}`, "_blank");
        toast.info("PDF baixado — anexe-o na conversa do WhatsApp que foi aberta.");
      } else {
        toast.info("Comprovante em PDF baixado.");
      }
    } catch (err) {
      if (err && err.name === "AbortError") return;
      logError("Erro ao enviar comprovante:", err);
      toast.error("Erro ao enviar comprovante");
    }
  };

  const carregarReceitas = useCallback(async (search = buscaAplicada.current) => {
    try {
      setLoading(true);
      const params = search ? { search } : {};
      const response = await axios.get(`${API}/receitas`, { params });
      let filtradas = response.data;
      if (lojaSelecionada && lojaSelecionada !== "Todas as lojas") {
        filtradas = filtradas.filter((r) => r.nome_loja === lojaSelecionada);
      }
      if (statusFiltro !== "Todos") {
        filtradas = filtradas.filter((r) => (r.status || "Em Produção") === statusFiltro);
      }
      if (filtroDataAtivo && dataInicio) {
        const inicio = new Date(dataInicio + "T00:00:00");
        filtradas = filtradas.filter((r) => new Date(r.data_registro) >= inicio);
      }
      if (filtroDataAtivo && dataFim) {
        const fim = new Date(dataFim + "T23:59:59");
        filtradas = filtradas.filter((r) => new Date(r.data_registro) <= fim);
      }
      setReceitas(filtradas);
    } catch {
      toast.error("Erro ao carregar receitas");
    } finally {
      setLoading(false);
    }
  }, [lojaSelecionada, statusFiltro, filtroDataAtivo, dataInicio, dataFim]);

  useEffect(() => {
    carregarReceitas();
  }, [carregarReceitas]);

  const handleSearch = () => { buscaAplicada.current = searchTerm; carregarReceitas(searchTerm); };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/receitas/${deleteDialog.id}`);
      toast.success("Receita excluida com sucesso");
      setDeleteDialog({ open: false, id: null });
      carregarReceitas(searchTerm);
    } catch {
      toast.error("Erro ao excluir receita");
    }
  };

  const exportarDados = () => {
    const BOM = "\uFEFF";
    const sep = ";";
    const header = [
      "OS", "Data", "Cliente", "CPF", "Telefone",
      "OD ESF", "OD CIL", "OD EIXO", "OD ADICAO", "DNP OD", "Altura OD",
      "OE ESF", "OE CIL", "OE EIXO", "OE ADICAO", "DNP OE", "Altura OE",
      "Laboratorio", "Tipo Lente", "Valor Lente (R$)", "Valor Total (R$)",
      "Forma Pagamento", "Loja", "Status", "Validade", "Observacoes"
    ];
    const rows = receitas.map((r) => {
      const v = getValidadeBadge(r);
      return [
        r.os || "", new Date(r.data_registro).toLocaleDateString("pt-BR"),
        r.nome_cliente || "", r.cpf_cliente || "", r.telefone_cliente || "",
        r.od_esf || "", r.od_cil || "", r.od_eixo || "", r.od_adicao || "", r.dnp_od || "", r.altura_od || "",
        r.oe_esf || "", r.oe_cil || "", r.oe_eixo || "", r.oe_adicao || "", r.dnp_oe || "", r.altura_oe || "",
        r.nome_laboratorio || "", r.tipo_lente || "",
        (r.valor_lente || 0).toFixed(2).replace(".", ","),
        (r.valor_total || 0).toFixed(2).replace(".", ","),
        r.forma_pagamento || "", r.nome_loja || "",
        r.status || "Em Produção", v.label, r.observacoes || ""
      ];
    });
    const csv = BOM + [header, ...rows].map((row) => row.join(sep)).join("\r\n");
    const blob = new Blob([csv], { type: "text-csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `receitas_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    toast.success("Dados exportados com grau da receita");
  };

  const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

  const fmtData = (d) => {
    if (!d) return "-";
    try {
      const dt = typeof d === "string" && d.length <= 10 ? new Date(d + "T12:00:00") : new Date(d);
      return dt.toLocaleDateString("pt-BR");
    } catch {
      return "-";
    }
  };

  if (!lojaSelecionadaGate) {
    const opcoes = [
      { value: "Todas as lojas", label: "Gerencial (todas as lojas)", gerencial: true },
      ...LOJAS_GATE.map((l) => ({ value: l, label: l })),
    ];
    const escolher = (v) => { setLojaSelecionada(v); setLojaSelecionadaGate(true); };
    return (
      <div className="max-w-2xl mx-auto" data-testid="receitas-loja-gate">
        <div className="text-center mb-8 mt-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-slate-900 text-white mb-4"><Store className="w-7 h-7" /></div>
          <h1 className="text-3xl font-bold text-slate-900">Receitas — Selecione a loja</h1>
          <p className="text-sm text-slate-500 mt-1">Escolha uma loja para ver as receitas ou entre no modo gerencial</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {opcoes.map((o) => (
            <button key={o.value} onClick={() => escolher(o.value)}
              data-testid={`receitas-gate-${o.value.toLowerCase().replace(/ /g, "-")}`}
              className={`flex items-center gap-3 p-4 rounded-xl border transition-all text-left hover:shadow-md ${
                o.gerencial ? "border-slate-900 bg-slate-900 text-white sm:col-span-2 hover:bg-slate-800" : "border-slate-200 bg-white text-slate-800 hover:border-slate-400"
              }`}>
              <Store className={`w-5 h-5 shrink-0 ${o.gerencial ? "text-white" : "text-slate-500"}`} />
              <span className="font-semibold">{o.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="receitas-container">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap gap-4 justify-between items-start sm:items-center">
          <div className="flex-1 flex gap-2 w-full sm:w-auto min-w-0">
            <Input
              placeholder="Buscar por nome, CPF, telefone ou OS..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="flex-1 min-w-0"
              data-testid="search-input"
            />
            <Button onClick={handleSearch} data-testid="search-button" className="active:scale-95 transition-transform flex-shrink-0">
              <Search className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button onClick={() => navigate("/receitas/lixeira")} variant="outline" data-testid="lixeira-button" className="active:scale-95 transition-transform">
              <Trash2 className="w-4 h-4 mr-2" /> Lixeira
            </Button>
            <Button onClick={exportarDados} variant="outline" disabled={receitas.length === 0} data-testid="export-button" className="active:scale-95 transition-transform">
              <Download className="w-4 h-4 mr-2" /> Exportar
            </Button>
            <Button onClick={() => navigate("/receitas/nova")} data-testid="new-receita-button" className="active:scale-95 transition-transform">
              <Plus className="w-4 h-4 mr-2" /> Nova Receita
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap items-end gap-3">
          <LojaSelector lojaSelecionada={lojaSelecionada} onLojaChange={setLojaSelecionada} />
          <Select value={statusFiltro} onValueChange={setStatusFiltro}>
            <SelectTrigger className="w-52" data-testid="filtro-status">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Todos">Todos os Status</SelectItem>
              <SelectItem value="Em Produção">Em Producao</SelectItem>
              <SelectItem value="Pronto para Entrega">Pronto para Entrega</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-end gap-2">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">De</Label>
              <Input
                type="date"
                value={dataInicio}
                onChange={(e) => { setDataInicio(e.target.value); setFiltroDataAtivo(true); }}
                className="w-40 h-9"
                data-testid="filtro-data-inicio"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Ate</Label>
              <Input
                type="date"
                value={dataFim}
                onChange={(e) => { setDataFim(e.target.value); setFiltroDataAtivo(true); }}
                className="w-40 h-9"
                data-testid="filtro-data-fim"
              />
            </div>
            {filtroDataAtivo && (
              <Button
                variant="ghost"
                size="sm"
                className="h-9 text-xs text-slate-500"
                onClick={() => { setDataInicio(""); setDataFim(""); setFiltroDataAtivo(false); }}
                data-testid="limpar-filtro-data"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Limpar
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <LoadingSpinner message="Carregando receitas..." />
      ) : receitas.length === 0 ? (
        <Card className="shadow-sm" data-testid="empty-state">
          <CardContent className="py-12 text-center">
            <p className="text-slate-500 mb-4">Nenhuma receita encontrada</p>
            <Button onClick={() => navigate("/receitas/nova")} data-testid="empty-new-receita-button">
              <Plus className="w-4 h-4 mr-2" /> Criar primeira receita
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4" data-testid="receitas-list">
          {receitas.map((receita) => {
            const validade = getValidadeBadge(receita);
            const atrasado = isAtrasado(receita);
            const statusLabel = receita.status || "Em Produção";
            const isProducao = statusLabel === "Em Produção";
            return (
              <Card
                key={receita.id}
                className={`bg-white border shadow-sm hover:shadow-md transition-shadow duration-200 ${atrasado ? "border-red-300 ring-1 ring-red-200" : "border-slate-200"}`}
                data-testid={`receita-card-${receita.id}`}
              >
                <CardHeader className="bg-slate-50 border-b border-slate-200">
                  <div className="flex justify-between items-start">
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-lg font-semibold text-slate-900">
                        {receita.nome_cliente || "Cliente nao informado"}
                      </CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        OS: {receita.os || "N/A"} | Data: {new Date(receita.data_registro).toLocaleDateString("pt-BR")}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {atrasado && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border bg-red-100 text-red-700 border-red-300 animate-pulse" data-testid={`badge-atrasado-${receita.id}`}>
                            <AlertTriangle className="w-3 h-3" /> Atrasado +7 dias
                          </span>
                        )}
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${validade.color}`} data-testid={`badge-validade-${receita.id}`}>
                          <Clock className="w-3 h-3" /> {validade.label}
                        </span>
                        <button
                          onClick={() => toggleStatus(receita)}
                          className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border cursor-pointer hover:opacity-80 transition-opacity ${isProducao ? "bg-blue-100 text-blue-700 border-blue-200" : "bg-emerald-100 text-emerald-700 border-emerald-200"}`}
                          data-testid={`badge-status-${receita.id}`}
                          title="Clique para alternar o status"
                        >
                          {isProducao ? <Package className="w-3 h-3" /> : <CheckCircle className="w-3 h-3" />}
                          {statusLabel}
                        </button>
                        {receita.fotos?.length > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border bg-slate-100 text-slate-600 border-slate-200">
                            <Camera className="w-3 h-3" /> {receita.fotos.length} foto{receita.fotos.length > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => gerarComprovante(receita)}
                        title="Gerar comprovante de compra (PDF)"
                        data-testid={`pdf-button-${receita.id}`}
                      >
                        <FileText className="w-4 h-4 text-emerald-600" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => enviarComprovanteWhatsApp(receita)}
                        title="Enviar comprovante ao cliente pelo WhatsApp"
                        data-testid={`whatsapp-comprovante-${receita.id}`}
                      >
                        <MessageCircle className="w-4 h-4 text-green-600" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => navigate(`/receitas/nova`, { state: { duplicarDe: receita } })} 
                        title="Duplicar OS (copiar dados do cliente e receita)"
                        data-testid={`duplicate-button-${receita.id}`}
                      >
                        <Copy className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => navigate(`/receitas/editar/${receita.id}`)} data-testid={`edit-button-${receita.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteDialog({ open: true, id: receita.id })} data-testid={`delete-button-${receita.id}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6 overflow-hidden">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm mb-6">
                    <div><p className="text-slate-500 text-xs">CPF</p><p className="font-medium text-slate-900">{receita.cpf_cliente || "-"}</p></div>
                    <div><p className="text-slate-500 text-xs">Telefone</p><p className="font-medium text-slate-900">{receita.telefone_cliente || "-"}</p></div>
                    <div><p className="text-slate-500 text-xs">Laboratorio</p><p className="font-medium text-slate-900">{receita.nome_laboratorio || "-"}</p></div>
                    <div><p className="text-slate-500 text-xs">Tipo de Lente</p><p className="font-medium text-slate-900">{receita.tipo_lente || "-"}</p></div>
                    <div><p className="text-slate-500 text-xs">Valor Total</p><p className="font-medium text-slate-900">{fmt(receita.valor_total)}</p></div>
                    <div><p className="text-slate-500 text-xs">Forma de Pagamento</p><p className="font-medium text-slate-900">
                      {receita.formas_pagamento?.length > 0 
                        ? receita.formas_pagamento.filter(fp => fp.forma !== "Pendente").map(fp => `${fp.forma} ${fmt(fp.valor)}`).join(", ") || receita.forma_pagamento || "-"
                        : receita.forma_pagamento || "-"}
                      {receita.forma_pagamento === "Credito" && receita.parcelas ? ` (${receita.parcelas}x)` : ""}
                    </p></div>
                    <div><p className="text-slate-500 text-xs">Loja</p><p className="font-medium text-slate-900">{receita.nome_loja || "-"}</p></div>
                    <div data-testid={`data-pgto-inicial-${receita.id}`}><p className="text-slate-500 text-xs">Data Pgto Inicial</p><p className="font-medium text-slate-900">{fmtData(receita.data_venda || receita.data_registro)}</p></div>
                    {receita.data_quitacao && (
                      <div data-testid={`data-pgto-final-${receita.id}`}><p className="text-emerald-600 text-xs font-medium">Data Pgto Final (quitação)</p><p className="font-medium text-emerald-700">{fmtData(receita.data_quitacao)}</p></div>
                    )}
                    {receita.tipo_produto && (
                      <div><p className="text-slate-500 text-xs">Produto</p><p className="font-medium text-slate-900">{receita.tipo_produto}</p></div>
                    )}
                    {receita.incluir_relatorio_lentes === false && (
                      <div><p className="text-blue-600 text-xs font-medium">Sem lente</p><p className="font-medium text-blue-700 text-xs">Nao inclui no relatorio</p></div>
                    )}
                    {receita.falta_pagamento && (
                      <div><p className="text-amber-600 text-xs font-medium">Pagamento Pendente</p><p className="font-medium text-amber-700">{receita.falta_pagamento}</p></div>
                    )}
                  </div>

                  {(() => {
                    const itens = [];
                    if (receita.tipo_produto) {
                      const det = [receita.estilo_armacao, receita.referencia, receita.tipo_lente, receita.descricao_servico, receita.tipo_acessorio].filter(Boolean).join(" · ");
                      itens.push({ nome: receita.tipo_produto + (det ? ` (${det})` : ""), valor: receita.valor_lente });
                    }
                    (receita.produtos_adicionais || []).forEach((p) => {
                      const det = [p.estilo_armacao, p.referencia, p.tipo_lente, p.descricao_servico, p.tipo_acessorio].filter(Boolean).join(" · ");
                      itens.push({ nome: (p.tipo || "Produto") + (det ? ` (${det})` : ""), valor: p.valor });
                    });
                    if (itens.length === 0) return null;
                    return (
                      <div className="mb-4 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid={`produtos-resumo-${receita.id}`}>
                        <p className="text-slate-700 text-xs font-semibold mb-2">Produtos adquiridos</p>
                        <div className="space-y-1">
                          {itens.map((it, i) => (
                            <div key={`prod-${i}`} className="flex items-center justify-between text-xs gap-3" data-testid={`produto-resumo-item-${receita.id}-${i}`}>
                              <span className="text-slate-600">{it.nome}</span>
                              <span className="font-semibold text-slate-900 whitespace-nowrap">{(it.valor !== undefined && it.valor !== null && it.valor !== "") ? fmt(Number(it.valor)) : "-"}</span>
                            </div>
                          ))}
                          <div className="flex items-center justify-between text-xs pt-1.5 mt-1 border-t border-slate-200">
                            <span className="text-slate-500 font-medium">Total</span>
                            <span className="font-bold text-slate-900">{fmt(receita.valor_total)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {receita.quitacoes?.length > 0 && (
                    <div className="mb-4 bg-emerald-50 border border-emerald-100 rounded-lg p-3" data-testid={`quitacoes-hist-${receita.id}`}>
                      <p className="text-emerald-700 text-xs font-semibold mb-2">Quitações realizadas</p>
                      <div className="space-y-1">
                        {receita.quitacoes.map((q, i) => (
                          <div key={`${q.data}-${q.valor}-${i}`} className="flex items-center justify-between text-xs" data-testid={`quitacao-item-${receita.id}-${i}`}>
                            <span className="text-slate-600">{fmtData(q.data)} · {q.forma}</span>
                            <span className="font-semibold text-emerald-700">{fmt(q.valor)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Graus */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 min-w-0">
                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 sm:p-4 min-w-0 overflow-hidden">
                      <h4 className="text-sm font-semibold text-blue-700 mb-3">OD - Olho Direito</h4>
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        {[["ESF", receita.od_esf], ["CIL", receita.od_cil], ["EIXO", receita.od_eixo], ["ADICAO", receita.od_adicao]].map(([l, v]) => (
                          <div key={l} className="min-w-0"><p className="text-slate-500 mb-1">{l}</p><p className="font-mono font-medium text-slate-900 bg-white px-1.5 py-1 rounded border border-blue-200 truncate text-center">{v || "-"}</p></div>
                        ))}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                        {[["DNP", receita.dnp_od], ["Altura", receita.altura_od]].map(([l, v]) => (
                          <div key={l} className="min-w-0"><p className="text-slate-500 mb-1">{l}</p><p className="font-mono font-medium text-slate-900 bg-white px-1.5 py-1 rounded border border-blue-200 truncate text-center">{v || "-"}</p></div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-green-50 border border-green-100 rounded-lg p-3 sm:p-4 min-w-0 overflow-hidden">
                      <h4 className="text-sm font-semibold text-green-700 mb-3">OE - Olho Esquerdo</h4>
                      <div className="grid grid-cols-4 gap-2 text-xs">
                        {[["ESF", receita.oe_esf], ["CIL", receita.oe_cil], ["EIXO", receita.oe_eixo], ["ADICAO", receita.oe_adicao]].map(([l, v]) => (
                          <div key={l} className="min-w-0"><p className="text-slate-500 mb-1">{l}</p><p className="font-mono font-medium text-slate-900 bg-white px-1.5 py-1 rounded border border-green-200 truncate text-center">{v || "-"}</p></div>
                        ))}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                        {[["DNP", receita.dnp_oe], ["Altura", receita.altura_oe]].map(([l, v]) => (
                          <div key={l} className="min-w-0"><p className="text-slate-500 mb-1">{l}</p><p className="font-mono font-medium text-slate-900 bg-white px-1.5 py-1 rounded border border-green-200 truncate text-center">{v || "-"}</p></div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Fotos */}
                  {receita.fotos?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-slate-500 text-xs mb-2">Fotos Anexadas</p>
                      <div className="flex gap-2 flex-wrap">
                        {receita.fotos.map((url, i) => (
                          <a key={url} href={url.startsWith("http") ? url : `${BACKEND_URL}${url}`} target="_blank" rel="noopener noreferrer">
                            <img src={url.startsWith("http") ? url : `${BACKEND_URL}${url}`} alt={`Foto ${i + 1}`} className="w-20 h-20 object-cover rounded-lg border border-slate-200 hover:opacity-80 transition-opacity" data-testid={`foto-thumb-${receita.id}-${i}`} />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {receita.observacoes && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <p className="text-slate-500 text-xs">Observacoes</p>
                      <p className="text-slate-900 text-sm mt-1">{receita.observacoes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusao</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja excluir esta receita? Esta acao nao pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-button">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Receitas;

